import {
  getContentDatabase,
  getOnlineContentDatabase,
  type ContentDatabaseConnection,
  type ContentDatabaseHandle,
} from "@-/dev.improve.haniverse.database.content";
import {
  getLevelPackConnection,
  getLevelPackIdentity,
  getPackDataIdentity,
  usePackDataIdentity,
} from "@-/dev.improve.haniverse.lib.database.shards";
import { resolveHanWorkerUrl } from "@-/dev.improve.haniverse.lib.worker";
import { useCallback } from "react";
import { onlineManager } from "@-/com.npmjs.tanstack__react-query";

export type GetContentDatabase = () => Promise<ContentDatabaseHandle>;
export type GetContentDatabaseConnection = () => Promise<ContentDatabaseConnection | null>;
interface ContentDatabaseLoadResult {
  connection: ContentDatabaseConnection | null;
  database: ContentDatabaseHandle;
}
const contentDatabaseCache = new Map<string, ContentDatabaseLoadResult>();
export function setCachedContentDatabase(result: ContentDatabaseLoadResult) {
  contentDatabaseCache.set(getPackDataIdentity(), result);
}
async function loadContentDatabase(identity: string): Promise<ContentDatabaseLoadResult> {
  const seeded = contentDatabaseCache.get(identity);
  if (seeded !== undefined) {
    return seeded;
  }
  if (onlineManager.isOnline()) {
    return { connection: null, database: getOnlineContentDatabase(resolveHanWorkerUrl()) };
  }
  const connection = await getLevelPackConnection(getLevelPackIdentity());
  if (connection === null) {
    throw new Error(
      "No level packs are downloaded. Connect to download a pack in Settings → Offline Packs.",
    );
  }
  return { connection, database: getContentDatabase(connection) };
}
export function clearCachedContentDatabase(): void {
  contentDatabaseCache.clear();
}
export function useGetContentDatabase(): GetContentDatabase {
  const identity = usePackDataIdentity();
  return useCallback(async () => {
    const { database } = await loadContentDatabase(identity);
    return database;
  }, [identity]);
}
export function useGetContentDatabaseConnection(): GetContentDatabaseConnection {
  const identity = usePackDataIdentity();
  return useCallback(async () => {
    const { connection } = await loadContentDatabase(identity);
    return connection;
  }, [identity]);
}
